/**
 * 列印推播的連線層防護。
 *
 * 這裡處理的是「同一則推播被投遞不只一次」與「連線該關卻關不掉」，
 * 兩者都源自同一件事：後端的列印推播是逐連線發送的（每個 BACK 連線各送一次），
 * 所以一個 WebView 只要同時存在兩條 STOMP 連線，同一張單就會被處理兩次。
 */
// 用相對路徑而非 @ 別名：本檔會被 vitest 直接載入，而測試環境沒有配那個別名
import { pruneMessageMap } from './messageMap'

/**
 * 「這則推播剛剛才收過」的判定視窗（毫秒）。
 *
 * 後端保證同一個 messageId 兩次推播至少間隔 12 秒——定時掃描與重連補送兩條路徑
 * 都吃 `last_push_time <= now - MIN_PUSH_GAP_SECONDS(12)` 這個條件。
 * 只要視窗小於 12 秒就不可能誤擋補送；取 5 秒是留餘裕給時鐘誤差與排程抖動。
 * 後端調小那個常數時，這裡要跟著調。
 */
export const RECENT_PUSH_TTL_MS = 5 * 1000

/**
 * deactivate 的等待上限（毫秒）。
 *
 * stompjs 6 關 socket 的方式是送出 DISCONNECT 之後等 receipt 回來才真的關，而且沒有逾時。
 * WebView 被系統凍住、或網路已經斷掉時那個 receipt 永遠不會回來，disconnect 就卡在那裡。
 */
export const DEACTIVATE_TIMEOUT_MS = 3000

/**
 * 這則推播是不是剛剛才收過、應該直接丟掉。
 *
 * 判定必須是同步的，而且要排在所有 ACK、平台分流與 await 之前，讓同一則推送
 * 從多條 STOMP 連線同時抵達時，只會有第一則進入後續流程。
 *
 * @param {Map<string, number>} recentPushes 收過的 messageId → 收到時間
 * @param {string} messageId 本次推播的 messageId，沒有就一律放行
 * @param {number} [now] 現在時間，測試用
 * @returns {boolean} true 表示重複投遞、應該丟棄；false 表示放行並已記錄
 */
export const shouldSkipDuplicatePush = (
	recentPushes,
	messageId,
	now = Date.now(),
) => {
	if (!messageId) {
		return false
	}

	const seenAt = recentPushes.get(messageId)
	if (seenAt !== undefined && now - seenAt < RECENT_PUSH_TTL_MS) {
		return true
	}

	recentPushes.set(messageId, now)
	pruneMessageMap(recentPushes, RECENT_PUSH_TTL_MS, now)
	return false
}

/**
 * 關掉一條連線，逾時就直接砍斷底層 socket。
 *
 * 不砍的話那條連線會留在後端的連線盤點裡直到心跳逾時清理（預設 5 分鐘），
 * 期間它仍會收到列印推播。
 *
 * @param {object} client 要關閉的 stompjs Client
 * @param {number} [timeoutMs] 等待上限
 * @returns {Promise<void>}
 */
export const deactivateWithTimeout = async (
	client,
	timeoutMs = DEACTIVATE_TIMEOUT_MS,
) => {
	let timer = null
	try {
		await Promise.race([
			Promise.resolve(client.deactivate()).catch(() => {}),
			new Promise(resolve => {
				timer = setTimeout(resolve, timeoutMs)
			}),
		])
	} finally {
		clearTimeout(timer)
	}

	// 沒能在期限內關掉就強制斷線；已經關好的話這段不會執行
	if (client.connected || client.active) {
		try {
			client.forceDisconnect()
		} catch (err) {
			// 強制斷線失敗沒有補救手段，也不該讓 disconnect 整條掛掉
		}
	}
}
